/**
 * Helpers de formatação de data/hora compartilhados pelas páginas administrativas.
 *
 * Equivalente Angular/Java:
 * - relativeTime ≈ DatePipe + custom transform
 * - fullDate ≈ Java DateTimeFormatter.ofPattern("dd/MM/yyyy HH:mm:ss")
 */

/**
 * Retorna a distância humanizada entre o instante e agora.
 * Exemplos: "agora", "há 5 min", "há 3h", "há 2d".
 */
export function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'agora';
  if (diff < 3_600_000) return `há ${Math.floor(diff / 60_000)} min`;
  if (diff < 86_400_000) return `há ${Math.floor(diff / 3_600_000)}h`;
  return `há ${Math.floor(diff / 86_400_000)}d`;
}

type DateInput = string | number | Date;

/**
 * Retorna a data completa formatada em pt-BR (dd/MM/yyyy HH:mm:ss).
 * Em caso de erro de parsing, devolve a entrada original como string.
 */
export function fullDate(value: DateInput): string {
  try {
    return new Date(value).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return String(value);
  }
}

/**
 * Apenas a data em pt-BR (dd/MM/yyyy).
 */
export function dateOnly(value: DateInput): string {
  try {
    return new Date(value).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return String(value);
  }
}

/**
 * Apenas o horário em pt-BR (HH:mm, ou HH:mm:ss com ``withSeconds``). Sempre 24h.
 */
export function timeOnly(value: DateInput, withSeconds = false): string {
  try {
    return new Date(value).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      ...(withSeconds ? { second: '2-digit' as const } : {}),
    });
  } catch {
    return String(value);
  }
}
